import { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  IconButton,
  FormHelperText,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ClearIcon from '@mui/icons-material/Clear';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import {
  FamilyStory,
  Person,
  UpdateStoryPayload,
} from '../../../../types/tree';
import { useModal } from '../../../../components/common';
import DeleteStory from './DeleteStory';
import { StoryForm } from '../../../../types/tree';
import { topTags } from '../constants';
import { useAuth } from '../../../../components/auth/AuthProvider';
import {
  getCharacterCountHelperText,
  hasReachedCharacterLimit,
  sanitiseNameInput,
  sanitiseTextInput,
} from '../../../../utils';
import { textLimits } from '../../../../core/constants/constants';

type StoryDetailsProps = {
  story?: FamilyStory | null;
  treeMembers?: Person[];
  onDeleteStory: () => void;
  onUpdateStory: (payload: UpdateStoryPayload | FormData) => void;
};

const isImageUrl = (mediaUrl: string) =>
  /\.(jpe?g|png|webp)(?:[?#]|$)/i.test(mediaUrl);

export default function StoryDetails({
  story,
  treeMembers = [],
  onDeleteStory,
  onUpdateStory,
}: StoryDetailsProps) {
  const { openModal, closeModal } = useModal();

  const { control, handleSubmit, setValue, reset } = useForm<
    StoryForm & { member_ids: string[] }
  >({
    defaultValues: {
      title: story?.title ?? '',
      content: story?.content ?? '',
      story_date: story?.story_date ?? '',
      story_location: story?.story_location ?? '',
      tags: story?.tags ?? [],
      member_ids: story?.member_ids ?? [],
      is_public: story?.is_public ?? false,
    },
  });
  const [isEditing, setIsEditing] = useState(false);
  const [retainedMediaUrls, setRetainedMediaUrls] = useState<string[]>([]);
  const [selectedMediaFiles, setSelectedMediaFiles] = useState<File[]>([]);
  const [mediaError, setMediaError] = useState('');
  const { currentUser } = useAuth();

  useEffect(() => {
    if (story) {
      reset({
        title: story.title ?? '',
        content: story.content ?? '',
        story_date: story.story_date ?? '',
        story_location: story.story_location ?? '',
        tags: story.tags ?? [],
        member_ids: story.member_ids ?? [],
        is_public: story.is_public ?? false,
      });
      setRetainedMediaUrls(story.media_urls ?? []);
      setSelectedMediaFiles([]);
      setMediaError('');
    }
  }, [story, reset]);

  if (!story)
    return (
      <Box p={2}>
        <Typography variant="h6">
          Add your family stories to see details here.
        </Typography>
      </Box>
    );

  function openDeleteModal(): void {
    openModal(<DeleteStory closeModal={closeModal} onDelete={onDeleteStory} />);
  }

  function handleMediaSelection(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    const allowedFiles = files.filter((file) =>
      [
        'image/jpeg',
        'image/png',
        'image/webp',
        'audio/aac',
        'audio/mp3',
        'audio/mpeg',
        'audio/mp4',
        'audio/ogg',
        'audio/wav',
        'audio/x-m4a',
        'audio/x-wav',
      ].includes(file.type),
    );
    const totalImageCount =
      retainedMediaUrls.filter(isImageUrl).length +
      selectedMediaFiles.filter((file) => file.type.startsWith('image/'))
        .length +
      allowedFiles.filter((file) => file.type.startsWith('image/')).length;
    const hasOversizedImage = allowedFiles.some(
      (file) => file.type.startsWith('image/') && file.size > 25 * 1024 * 1024,
    );
    const hasOversizedAudio = allowedFiles.some(
      (file) => file.type.startsWith('audio/') && file.size > 50 * 1024 * 1024,
    );

    if (allowedFiles.length !== files.length) {
      setMediaError('Choose JPG, PNG, WEBP, MP3, WAV, OGG, M4A, or AAC files.');
    } else if (hasOversizedImage) {
      setMediaError('Image files must be 25MB or smaller.');
    } else if (hasOversizedAudio) {
      setMediaError('Audio files must be 50MB or smaller.');
    } else if (totalImageCount > 3) {
      setMediaError('A story can contain up to 3 photos.');
    } else {
      setMediaError('');
      setSelectedMediaFiles((currentFiles) => [
        ...currentFiles,
        ...allowedFiles,
      ]);
    }
    event.target.value = '';
  }

  function onSubmit(data: any) {
    if (!currentUser) {
      return;
    }
    const values: UpdateStoryPayload = {
      title: sanitiseNameInput(data.title),
      content: sanitiseTextInput(data.content),
      story_date: data.story_date ? sanitiseNameInput(data.story_date) : '',
      story_location: data.story_location
        ? sanitiseTextInput(data.story_location)
        : '',
      tags: data.tags,
      member_ids: data.member_ids ?? story?.member_ids ?? [],
      is_public: data.is_public ?? story?.is_public ?? false,
      updated_by: currentUser.uid,
    };

    const originalMediaUrls = story?.media_urls ?? [];
    const mediaChanged =
      selectedMediaFiles.length > 0 ||
      retainedMediaUrls.length !== originalMediaUrls.length ||
      retainedMediaUrls.some(
        (mediaUrl) => !originalMediaUrls.includes(mediaUrl),
      );

    if (mediaChanged) {
      const formData = new FormData();
      formData.append('title', values.title ?? '');
      formData.append('content', values.content ?? '');
      formData.append('story_date', values.story_date ?? '');
      formData.append('story_location', values.story_location ?? '');
      formData.append('is_public', String(Boolean(values.is_public)));
      formData.append('updated_by', values.updated_by ?? '');
      (values.tags ?? []).forEach((tag) => formData.append('tags', tag));
      (values.member_ids ?? []).forEach((memberId) =>
        formData.append('member_ids', memberId),
      );
      retainedMediaUrls.forEach((mediaUrl) =>
        formData.append('media_urls', mediaUrl),
      );
      selectedMediaFiles.forEach((file) =>
        formData.append('media_files', file),
      );
      onUpdateStory(formData);
    } else {
      onUpdateStory(values);
    }

    setIsEditing(false);
  }

  return (
    <Box p={3}>
      {isEditing && (
        <Box
          sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            p: 2.5,
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            overflow: 'hidden',
            animation: 'storyEditFade 220ms cubic-bezier(0.2, 0, 0, 1)',
            '@keyframes storyEditFade': {
              '0%': { opacity: 0, transform: 'translateY(5px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Title"
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  sx={{
                    width: '100%',
                    maxWidth: '100%',
                    '& .MuiInputBase-root': {
                      maxWidth: '100%',
                    },
                  }}
                  inputProps={{
                    maxLength: textLimits.storyTitle,
                    style: { overflowWrap: 'anywhere' },
                  }}
                />
              )}
            />
            <Controller
              name="content"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Content"
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={5}
                  margin="normal"
                  inputProps={{ maxLength: textLimits.storyContent }}
                  error={hasReachedCharacterLimit(
                    field.value,
                    textLimits.storyContent,
                  )}
                  helperText={getCharacterCountHelperText(
                    field.value,
                    textLimits.storyContent,
                  )}
                />
              )}
            />
            <Controller
              name="story_date"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Story date"
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  placeholder="e.g. 1950 or June 1967"
                />
              )}
            />
            <Controller
              name="story_location"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Story location"
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  placeholder="e.g. Lagos, Nigeria"
                />
              )}
            />
            <Controller
              name="tags"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  freeSolo
                  options={topTags}
                  value={field.value}
                  onChange={(_, newValue) => setValue('tags', newValue)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tags"
                      InputLabelProps={{ shrink: true }}
                      placeholder="Add tags"
                      fullWidth
                      margin="dense"
                      helperText="You can add new tags e.g. marriage, tradition, name, etc."
                    />
                  )}
                />
              )}
            />
            <Controller
              name="member_ids"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  options={treeMembers}
                  getOptionLabel={(member) =>
                    [member.first_name, member.middle_name, member.last_name]
                      .filter(Boolean)
                      .join(' ')
                  }
                  value={treeMembers.filter((member) =>
                    (field.value ?? []).includes(member.id),
                  )}
                  onChange={(_, newValue) =>
                    setValue(
                      'member_ids',
                      newValue.map((member) => member.id),
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Related family members"
                      InputLabelProps={{ shrink: true }}
                      placeholder="Select family members"
                      fullWidth
                      margin="dense"
                      helperText="Choose people this story relates to"
                    />
                  )}
                />
              )}
            />
            <Box
              component="section"
              aria-labelledby="edit-story-media"
              sx={{ mt: 2 }}
            >
              <Typography
                id="edit-story-media"
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Media attachments
              </Typography>
              {retainedMediaUrls.length > 0 && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: 1,
                    mt: 1,
                  }}
                >
                  {retainedMediaUrls.map((mediaUrl) => (
                    <Box
                      key={mediaUrl}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Typography variant="caption" noWrap>
                        {isImageUrl(mediaUrl) ? 'Photo' : 'Audio recording'}
                      </Typography>
                      <IconButton
                        aria-label={`Remove ${isImageUrl(mediaUrl) ? 'photo' : 'audio recording'}`}
                        color="error"
                        size="small"
                        onClick={() =>
                          setRetainedMediaUrls((mediaUrls) =>
                            mediaUrls.filter((url) => url !== mediaUrl),
                          )
                        }
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
              <Button
                component="label"
                variant="outlined"
                startIcon={<AddPhotoAlternateIcon />}
                sx={{ mt: 1.5, textTransform: 'none' }}
              >
                Add media
                <input
                  hidden
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp,audio/aac,audio/mpeg,audio/mp4,audio/ogg,audio/wav"
                  onChange={handleMediaSelection}
                />
              </Button>
              {selectedMediaFiles.length > 0 && (
                <Box
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}
                >
                  {selectedMediaFiles.map((file, index) => (
                    <Chip
                      key={`${file.name}-${index}`}
                      label={file.name}
                      onDelete={() =>
                        setSelectedMediaFiles((files) =>
                          files.filter((_, fileIndex) => fileIndex !== index),
                        )
                      }
                      size="small"
                    />
                  ))}
                </Box>
              )}
              <FormHelperText>
                Up to 3 photos per story. Images: 25MB each. Audio: 50MB each.
              </FormHelperText>
              {mediaError && (
                <FormHelperText error>{mediaError}</FormHelperText>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                mt: 2,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Button
                onClick={() => {
                  reset({
                    title: story?.title ?? '',
                    content: story?.content ?? '',
                    story_date: story?.story_date ?? '',
                    story_location: story?.story_location ?? '',
                    tags: story?.tags ?? [],
                    member_ids: story?.member_ids ?? [],
                    is_public: story?.is_public ?? false,
                  });
                  setRetainedMediaUrls(story?.media_urls ?? []);
                  setSelectedMediaFiles([]);
                  setMediaError('');
                  setIsEditing(false);
                }}
                variant="outlined"
                sx={{
                  minHeight: 40,
                  px: 2,
                  borderRadius: 2,
                  textTransform: 'none',
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{
                  minHeight: 40,
                  px: 2,
                  borderRadius: 2,
                  textTransform: 'none',
                }}
              >
                Save changes
              </Button>
            </Box>
          </form>
        </Box>
      )}
      {!isEditing && (
        <Box
          display="flex"
          gap={2}
          flexDirection={'column'}
          sx={{
            animation: 'storyReadFade 220ms cubic-bezier(0.2, 0, 0, 1)',
            '@keyframes storyReadFade': {
              '0%': { opacity: 0, transform: 'translateY(4px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1.25,
            }}
          >
            <Typography
              variant="h4"
              fontWeight={700}
              gutterBottom
              sx={{
                mb: 0,
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                maxWidth: '100%',
              }}
            >
              {story?.title}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <Button
                onClick={() => {
                  reset({
                    title: story?.title ?? '',
                    content: story?.content ?? '',
                    story_date: story?.story_date ?? '',
                    story_location: story?.story_location ?? '',
                    tags: story?.tags ?? [],
                    member_ids: story?.member_ids ?? [],
                    is_public: story?.is_public ?? false,
                  });
                  setRetainedMediaUrls(story?.media_urls ?? []);
                  setSelectedMediaFiles([]);
                  setMediaError('');
                  setIsEditing(true);
                }}
                variant="outlined"
                startIcon={<EditIcon />}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  minHeight: 40,
                  px: 2,
                  width: { xs: '50%', sm: 'auto' },
                }}
              >
                Edit
              </Button>
              <Button
                onClick={openDeleteModal}
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  minHeight: 40,
                  px: 2,
                  width: { xs: '50%', sm: 'auto' },
                  '&:hover': {
                    backgroundColor: 'error.lighter',
                  },
                }}
              >
                Delete
              </Button>
            </Box>
          </Box>

          {(story?.story_date || story?.story_location) && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {story?.story_date && (
                <Chip
                  label={`Date: ${story.story_date}`}
                  variant="outlined"
                  color="primary"
                />
              )}
              {story?.story_location && (
                <Chip
                  label={`Location: ${story.story_location}`}
                  variant="outlined"
                  color="secondary"
                />
              )}
            </Box>
          )}
          <Typography
            variant="body1"
            sx={{
              marginBottom: 2,
              whiteSpace: 'pre-line',
              overflowY: 'auto',
              maxHeight: '400px',
              wordBreak: 'break-word',
              color: 'text.secondary',
              lineHeight: 1.8,
            }}
          >
            {story?.content}
          </Typography>
          {story.media_urls?.length ? (
            <Box component="section" aria-labelledby="story-media-heading">
              <Typography
                id="story-media-heading"
                variant="subtitle1"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Media
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: 1.5,
                }}
              >
                {story.media_urls.map((mediaUrl, index) =>
                  isImageUrl(mediaUrl) ? (
                    <Box
                      key={mediaUrl}
                      component="a"
                      href={mediaUrl}
                      target="_blank"
                      rel="noreferrer"
                      sx={{
                        display: 'block',
                        borderRadius: 1,
                        overflow: 'hidden',
                      }}
                      aria-label={`Open story photo ${index + 1}`}
                    >
                      <Box
                        component="img"
                        src={mediaUrl}
                        alt={`Story attachment ${index + 1}`}
                        sx={{
                          display: 'block',
                          width: '100%',
                          aspectRatio: '4 / 3',
                          objectFit: 'cover',
                          borderRadius: 1,
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      key={mediaUrl}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1.25,
                      }}
                    >
                      <Typography
                        variant="caption"
                        component="p"
                        sx={{ mb: 0.75 }}
                      >
                        Audio recording {index + 1}
                      </Typography>
                      <Box
                        component="audio"
                        controls
                        preload="metadata"
                        src={mediaUrl}
                        sx={{ width: '100%' }}
                      />
                      <Typography
                        component="a"
                        href={mediaUrl}
                        target="_blank"
                        rel="noreferrer"
                        variant="caption"
                        sx={{ display: 'inline-block', mt: 0.75 }}
                      >
                        Open audio recording
                      </Typography>
                    </Box>
                  ),
                )}
              </Box>
            </Box>
          ) : null}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pb: 0.5 }}>
            {story?.tags?.map((tag, index) => (
              <Chip
                key={`story_chip_${index}`}
                label={tag}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  backgroundColor: 'background.default',
                }}
              />
            ))}
          </Box>
          {story?.member_ids?.length ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 1 }}>
              {story.member_ids.map((memberId) => {
                const member = treeMembers.find((item) => item.id === memberId);
                if (!member) return null;
                return (
                  <Chip
                    key={`member_chip_${member.id}`}
                    label={[
                      member.first_name,
                      member.middle_name,
                      member.last_name,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    variant="outlined"
                    color="primary"
                    sx={{ borderRadius: 2 }}
                  />
                );
              })}
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  );
}
